import React, { memo } from 'react';

import GradientDefs, { MetricColors } from '../metricMonthlySummaryCard/GradientDefs';
import { MetricMonthlySummaryCard } from '../metricMonthlySummaryCard/MetricMonthlySummaryCard';
import { MetricTimeSeriesBarGraph } from '../metricTimeSeriesBarGraph/MetricTimeSeriesBarGraph';
import MessageInfo from '../messageInfo/MessageInfo';
import { SadFolderIllustration } from '../../components/emptyStateIllustrations/EmptyStateIllustrations';
import { isTieredScorecard, getExceedsTarget } from '../../utils/metricAttainment';
import './NonPayoutMetricsPanel.scss';

interface PropTypes {
    metrics: any[];
    dailyMetricsData: any;
    monthlyMetricHistory: any;
    scorecardVersionId: number;
    employeeNumber: string;
    currentMonthLabel?: string;
    historyLabel?: string;
}

const NonPayoutMetricsPanel = memo(({
    metrics,
    dailyMetricsData,
    monthlyMetricHistory,
    scorecardVersionId,
    employeeNumber,
    currentMonthLabel,
    historyLabel,
}: PropTypes) => {
    const totalCount = metrics.length;
    const isScrollable = totalCount > 4;

    if (totalCount === 0) {
        return (
            <div className="non-payout-metrics-empty">
                <div className="non-payout-metrics-empty__illustration">
                    <SadFolderIllustration />
                </div>
                <div className="non-payout-metrics-empty__divider" />
                <p className="non-payout-metrics-empty__title">No additional metrics found for the Performance Period.</p>
            </div>
        );
    }

    return (
        <>
            <GradientDefs />
            <div className="d-flex justify-content-end pe-3">
                <span style={{ fontWeight: 500, color: '#495057', lineHeight: '1' }}>
                    Total Additional Metrics: <strong>{totalCount}</strong>
                </span>
            </div>
            <div
                style={
                    isScrollable
                        ? {
                              maxHeight: '1100px',
                              overflowY: 'auto',
                              overflowX: 'hidden',
                              scrollbarWidth: 'thin',
                              scrollbarColor: '#c1c1c1 transparent',
                          }
                        : undefined
                }
            >
                {metrics.map((metric: any, index: number) => {
                    const bgColor = metric?.metric_omitted
                        ? MetricColors[5].bgColor
                        : MetricColors[index % MetricColors.length].bgColor;
                    const tiered = isTieredScorecard(metric?.scorecard_calculation_rule_id);

                    const dailyEntry: any = (() => {
                        const allMatching: any[] = dailyMetricsData?.data?.filter(
                            (d: any) => d.label === metric.label
                        ) ?? [];
                        if (allMatching.length === 0) return null;
                        return {
                            ...allMatching[0],
                            dailyMetricSummaryList: allMatching
                                .flatMap((el: any) => el.dailyMetricSummaryList ?? [])
                                .sort((a: any, b: any) =>
                                    new Date(b.result_date).getTime() - new Date(a.result_date).getTime()
                                ),
                        };
                    })();

                    const historyEntry: any[] =
                        monthlyMetricHistory?.data?.filter(
                            (h: any) => h.label === metric.label
                        ) ?? [];

                    return (
                        <div
                            key={metric.label}
                            className="card shadow bg-body rounded mt-3 border-1"
                            style={{ borderBottom: '1.5px solid #e0e0e0' }}
                        >
                            <div
                                className="d-flex flex-nowrap"
                                style={{ backgroundColor: bgColor, alignItems: 'stretch', minHeight: '260px' }}
                            >
                                {/* Left — summary card (fixed width + height) */}
                                <div style={{ width: '220px', minWidth: '220px', maxWidth: '220px', flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1.5px solid #e0e0e0' }}>
                                    <MetricMonthlySummaryCard
                                        targetVal={metric.target}
                                        metricAttained={metric.metric_attained}
                                        metricNameVal={metric.label}
                                        metricTypeName={metric.metric_type_name}
                                        metricVal={metric.metric_value}
                                        metricDesc={metric.metric_desc}
                                        metricDirectionId={metric.metric_direction_id}
                                        onCardClick={() => undefined}
                                        selectedMetric={metric.label}
                                        index={index}
                                        metricOmitted={metric?.metric_omitted}
                                        variant={tiered ? 'tiered' : 'legacy'}
                                        attainmentRank={metric?.attainment_rank}
                                        attainmentLevelLabel={metric?.metric_attainment_level_label}
                                        targets={metric?.targets}
                                        min={metric?.min}
                                        max={metric?.max}
                                    />
                                </div>

                                {/* Right — bar graph */}
                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                    {dailyEntry ? (
                                        <MetricTimeSeriesBarGraph
                                            key={`${metric.label}-${scorecardVersionId}`}
                                            metricName={dailyEntry.label}
                                            metricId={dailyEntry.metric_id}
                                            scorecardVersionId={scorecardVersionId}
                                            employeeNumber={employeeNumber}
                                            target={dailyEntry.target}
                                            metricTypeName={dailyEntry.metric_type_name}
                                            dailyData={dailyEntry.dailyMetricSummaryList}
                                            metricDirectionId={dailyEntry.metric_direction_id}
                                            calculatedDate={dailyEntry.calculate_date}
                                            metricHistoryData={historyEntry}
                                            fromMetricDashboard={false}
                                            metricIndex={index % MetricColors.length}
                                            currentMonthLabel={currentMonthLabel}
                                            historyLabel={historyLabel}
                                            metricOmitted={metric?.metric_omitted}
                                            exceedsTarget={getExceedsTarget(dailyEntry.targets ?? metric?.targets)}
                                        />
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-center h-100 ps-4">
                                            <MessageInfo
                                                title="No chart data available."
                                                message="Daily data not found for this metric."
                                                rowHeight="180px"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
});

NonPayoutMetricsPanel.displayName = 'NonPayoutMetricsPanel';

export default NonPayoutMetricsPanel;
